import React, { useMemo } from 'react';
import {
  BookOpenText,
  BrainCircuit,
  CalendarRange,
  ClipboardList,
  Gavel,
  RefreshCw,
  ShieldAlert,
  Target,
} from 'lucide-react';
import SourceTrustBadge from '@/components/SourceTrustBadge';
import {
  buildHearingPrepView,
} from '@/lib/hearingPrep';

const SECTION_ICONS = {
  hearing_objective: Target,
  core_issues: BrainCircuit,
  chronology: CalendarRange,
  strongest_authorities: BookOpenText,
  weakest_points: ShieldAlert,
  likely_bench_questions: Gavel,
  rebuttal_notes: ShieldAlert,
  next_steps_before_hearing: ClipboardList,
  oral_submissions_outline: BookOpenText,
};

function SectionCard({ section }) {
  const Icon = SECTION_ICONS[section.section_id] || ClipboardList;
  const shouldShowStructuredItems = !section.user_edited && (section.items || []).length > 0;

  return (
    <article
      style={{
        borderRadius: '20px',
        border: '1px solid var(--border-default)',
        background: 'var(--bg-base)',
        padding: '18px',
        display: 'grid',
        gap: '12px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <span
            style={{
              width: '34px',
              height: '34px',
              borderRadius: '12px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(15,118,110,0.08)',
              color: '#0f766e',
              flexShrink: 0,
            }}
          >
            <Icon size={16} />
          </span>
          <div style={{ color: 'var(--text-primary)', fontWeight: 800 }}>
            {section.title}
          </div>
        </div>
        {section.user_edited ? (
          <span style={{ color: 'var(--text-muted)', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Edited
          </span>
        ) : null}
      </div>

      <div style={{ display: 'grid', gap: '10px' }}>
        {shouldShowStructuredItems ? (section.items || []).map((item, index) => (
          <div key={`${section.section_id}-${index}`} style={{ display: 'grid', gap: '8px' }}>
            <div style={{ color: 'var(--text-primary)', fontSize: '14px', lineHeight: 1.65 }}>
              {item.text}
            </div>
            {item.detail && item.detail !== item.text ? (
              <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6 }}>
                {item.detail}
              </div>
            ) : null}
          </div>
        )) : (
          <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6 }}>
            {section.content || 'Nothing has been surfaced here yet.'}
          </div>
        )}
      </div>

      {section.provenance_labels?.length ? (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {section.provenance_labels.map((label) => (
            <SourceTrustBadge key={`${section.section_id}-${label}`} label={label} compact />
          ))}
        </div>
      ) : null}
    </article>
  );
}

export default function HearingPrepWorkspace({
  hearingPack,
  activeMode,
  onChangeMode,
  onRegenerate,
  regenerating = false,
}) {
  const view = useMemo(() => buildHearingPrepView(hearingPack, activeMode), [hearingPack, activeMode]);

  return (
    <section
      id="hearing-prep"
      className="advoq-card"
      style={{
        padding: '24px',
        borderRadius: '28px',
        display: 'grid',
        gap: '18px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ display: 'grid', gap: '8px' }}>
          <div style={{ color: 'var(--text-muted)', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Hearing Prep Workspace
          </div>
          <div style={{ color: 'var(--text-primary)', fontSize: '28px', fontWeight: 900, lineHeight: 1.1 }}>
            {view.headline || 'Build a hearing-ready pack from the live matter.'}
          </div>
          <div style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.7, maxWidth: '860px' }}>
            Matter-aware, chronology-aware, and compact enough to use when the board is moving. This pack favors verified Indian authority, matter files, reviewed Jolly memory, and accepted Aina pressure-tests.
          </div>
        </div>
        <button type="button" className="app-chat-button" onClick={onRegenerate} disabled={regenerating}>
          <RefreshCw size={14} className={regenerating ? 'animate-spin' : undefined} />
          Refresh pack
        </button>
      </div>

      {view.source_summary?.length ? (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {view.source_summary.map((item) => (
            <SourceTrustBadge key={item.label} label={item.label} count={item.count} compact />
          ))}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {(view.mode_options || []).map((mode) => {
          const active = mode.mode_id === view.activeMode?.mode_id;
          return (
            <button
              key={mode.mode_id}
              type="button"
              onClick={() => onChangeMode?.(mode.mode_id)}
              style={{
                borderRadius: '999px',
                border: active ? '1px solid rgba(15,118,110,0.38)' : '1px solid var(--border-default)',
                background: active ? 'rgba(15,118,110,0.1)' : 'transparent',
                color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
                padding: '9px 13px',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {mode.label}
            </button>
          );
        })}
      </div>

      {view.activeMode?.description ? (
        <div
          style={{
            borderRadius: '18px',
            background: 'linear-gradient(135deg, rgba(15,118,110,0.08), rgba(255,255,255,0.92))',
            border: '1px solid rgba(15,118,110,0.16)',
            padding: '16px 18px',
            color: 'var(--text-primary)',
            lineHeight: 1.65,
          }}
        >
          {view.activeMode.description}
        </div>
      ) : null}

      <div style={{ display: 'grid', gap: '14px', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
        {(view.sections || []).map((section) => (
          <SectionCard key={section.section_id} section={section} />
        ))}
      </div>
    </section>
  );
}
